import { useMemo, useState } from 'react';
import type { LabelItem, LabelMappingChoice, LabelMappings, LabelRecovery, SleeveKey } from '@agio/core';
import { SLEEVES } from '@agio/core/domain';
import { fmtNum } from '../../utils.js';
import { Table, THeadRow, TH, TD, TR } from '../ui/table.js';

const IGNORE_HE = 'לא אפיק — להתעלם';
const SLEEVE_HE: Record<string, string> = Object.fromEntries(SLEEVES.map((s) => [s.column, s.displayNameHe]));
const CHOICE_HE = (c: LabelMappingChoice) => (c === 'ignore' ? IGNORE_HE : SLEEVE_HE[c] ?? c);

/** The choices the admin can make for one heading. */
type Choice = LabelMappingChoice | '';

/** A heading the admin decides here: one with no meaning yet, or one whose meaning came from this client's saved mapping. */
const editable = (i: LabelItem) => !i.total && ((i.sleeve === null && !i.ignored) || i.saved);
/** What an editable heading currently means, as a picker value. */
const currentChoice = (i: LabelItem): Choice => (i.ignored ? 'ignore' : i.sleeve ?? '');

/**
 * Column headings the system has no name for, next to the amounts under
 * them. The system may propose what each one means; the admin decides, and
 * the sum updates as they choose so they can see it reach the sheet's own
 * total before confirming. A heading this client's admin named before is
 * shown with its saved meaning and can be changed here. Nothing has loaded yet.
 */
export function LabelConfirm({
  recoveries,
  busy,
  onConfirm,
  onCancel,
}: {
  recoveries: LabelRecovery[];
  busy: boolean;
  onConfirm: (mappings: LabelMappings) => void;
  onCancel: () => void;
}) {
  // One choice per heading, shared across sheets: the same word means the same thing in every sheet of the file.
  const unknownLabels = useMemo(
    () => [...new Set(recoveries.flatMap((r) => r.items.filter(editable).map((i) => i.label)))],
    [recoveries],
  );
  const [choices, setChoices] = useState<Record<string, Choice>>(() => {
    const out: Record<string, Choice> = {};
    for (const label of unknownLabels) {
      const item = recoveries.flatMap((r) => r.items).find((i) => i.label === label)!;
      if (item.saved) {
        out[label] = currentChoice(item);
        continue;
      }
      const p = recoveries.find((r) => r.proposals[label] !== undefined)?.proposals[label];
      out[label] = p === undefined || p === 'unsure' ? '' : p;
    }
    return out;
  });
  const undecided = unknownLabels.filter((l) => !choices[l]);
  const note = recoveries.find((r) => r.note)?.note ?? null;

  const confirm = () => {
    const mappings: LabelMappings = {};
    for (const label of unknownLabels) if (choices[label]) mappings[label] = choices[label] as LabelMappingChoice;
    onConfirm(mappings);
  };

  return (
    <div role="alert" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <p className="font-semibold text-amber-900">בגיליון ריכוז יש כותרת שצריך לומר מה היא</p>
      <p className="mt-1 text-sm text-foreground">
        אמרו לאיזה אפיק שייך כל סכום, או שהוא אינו אפיק כלל. הבחירה נשמרת ללקוח הזה בלבד, וההעלאה הבאה לא תשאל שוב.
        שום דבר לא נקלט עדיין.
      </p>
      {note && <p className="mt-1 text-sm text-amber-900">{note}</p>}
      {recoveries.map((r) => (
        <SheetView key={r.sheet} r={r} choices={choices} onChoose={(label, c) => setChoices((prev) => ({ ...prev, [label]: c }))} />
      ))}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" disabled={busy || undecided.length > 0} onClick={confirm}
          className="rounded-md bg-primary px-6 py-2 font-semibold text-primary-foreground disabled:opacity-60">
          {busy ? 'קולט…' : 'אשרו את המיפוי וקלטו'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}
          className="rounded-md border border-input px-6 py-2 font-semibold disabled:opacity-60">
          ביטול
        </button>
        {undecided.length > 0 && (
          <span className="text-sm text-muted-foreground">בחרו משמעות ל: {undecided.map((l) => `"${l}"`).join(', ')}</span>
        )}
      </div>
    </div>
  );
}

function SheetView({ r, choices, onChoose }: { r: LabelRecovery; choices: Record<string, Choice>; onChoose: (label: string, c: Choice) => void }) {
  // What the sheet would read as under the choices so far: the sleeves not
  // decided here, plus every editable amount now pointed at a sleeve. The
  // sheet's total may cover the main row alone or every block, so — like the
  // upload itself — either match counts.
  const decidedHere = r.items.filter(editable);
  const alreadyCounted = decidedHere.filter((i) => i.sleeve !== null).reduce((a, i) => a + i.value, 0);
  const chosen = decidedHere.filter((i) => choices[i.label] && choices[i.label] !== 'ignore').reduce((a, i) => a + i.value, 0);
  const wouldRead = r.read_total - alreadyCounted + chosen;
  const wouldReadMainRow = r.main_row_total - alreadyCounted + chosen;
  const matches = (sum: number) => r.printed_total !== null && Math.abs(r.printed_total - sum) <= 1;
  const agrees = matches(wouldRead) || matches(wouldReadMainRow);
  return (
    <div className="mt-4 rounded border border-amber-200 bg-white p-3">
      <p className="text-sm">
        <span className="text-muted-foreground">גיליון: </span>
        <span dir="auto" className="font-medium">{r.sheet}</span>
        <span className="text-muted-foreground"> · תיק: </span>
        <span dir="auto" className="font-medium">{r.fund}</span>
      </p>
      <Table minWidth="16rem" className="mt-2">
        <THeadRow>
          <TH>כותרת בגיליון</TH>
          <TH>משמעות</TH>
          <TH align="end">סכום</TH>
        </THeadRow>
        <tbody>
          {r.items.map((i) => (
            <TR key={i.label}>
              <TD><span dir="auto">{i.label}</span></TD>
              <TD>
                {i.total ? (
                  <span className="text-muted-foreground">סה"כ שבגיליון</span>
                ) : editable(i) ? (
                  <ChoicePicker
                    label={i.label}
                    value={choices[i.label] ?? ''}
                    reason={i.saved ? `נשמר בעבר ללקוח הזה: ${CHOICE_HE(currentChoice(i) as LabelMappingChoice)}` : r.reasons[i.label]}
                    onChange={(c) => onChoose(i.label, c)}
                  />
                ) : i.ignored ? (
                  <span className="text-muted-foreground">{IGNORE_HE}</span>
                ) : (
                  <span>{SLEEVE_HE[i.sleeve!]}</span>
                )}
              </TD>
              <TD align="end" className="tabular-nums">{fmtNum(i.value)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-sm">
        <span className="text-muted-foreground">סה"כ לפי הבחירה: </span>
        <span className="font-bold tabular-nums">{fmtNum(wouldRead)}</span>
        {r.printed_total !== null ? (
          <span className={agrees ? 'text-emerald-700' : 'text-destructive'}>
            {agrees ? ' ✓ תואם לסה"כ שבגיליון' : ` ✗ שונה מהסה"כ שבגיליון (${fmtNum(r.printed_total)})`}
          </span>
        ) : (
          <span className="text-muted-foreground"> · בגיליון אין סה"כ להשוות אליו</span>
        )}
      </p>
    </div>
  );
}

function ChoicePicker({ label, value, reason, onChange }: { label: string; value: Choice; reason?: string; onChange: (c: Choice) => void }) {
  return (
    <span className="flex min-w-[9rem] max-w-[14rem] flex-col gap-1">
      <select
        aria-label={`משמעות של ${label}`}
        dir="rtl"
        value={value}
        onChange={(e) => onChange(e.target.value as Choice)}
        className="w-full max-w-[10rem] rounded-md border border-input bg-popover px-2 py-1 text-sm"
      >
        <option value="">בחרו…</option>
        {SLEEVES.map((s) => (
          <option key={s.sleeveId} value={s.column}>{CHOICE_HE(s.column as SleeveKey)}</option>
        ))}
        <option value="ignore">{IGNORE_HE}</option>
      </select>
      {reason && <span className="text-xs text-muted-foreground" dir="auto">{reason.startsWith('נשמר') ? reason : `הצעת המערכת: ${reason}`}</span>}
    </span>
  );
}
